import { onDocumentReady, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * <geo-popup>: geo-redirect region selector (tier 2) — ported from the Legacy Theme's
 * global/geo-popup.ts (deviation #84). Legacy rode Dawn's <modal-dialog>; Horizon's
 * DialogComponent force-closes on ESC/outside-click — the opposite of this popup's forced
 * choice — so this element drives a native <dialog> directly (showModal gives the focus
 * trap and top layer; the 'cancel' listener blocks ESC, and no backdrop-close is wired).
 *
 * For first-time visitors with no stored preference whose Shopify.country (Markets context)
 * matches data-target-countries, the dialog opens after a short delay. 'Continue to USA'
 * stores 'us' and redirects — tier 1 (the synchronous head script in layout/theme.liquid)
 * then redirects every later visit with zero flicker. 'Stay global' stores 'global' and closes.
 */

/* Crawlers and Google's testing agents never see the dialog. Keep in sync with the tier-1 head
 * script in layout/theme.liquid. inspectiontool|googleother (I18N-14): the regex ported from legacy
 * missed Google-InspectionTool (GSC live test, Rich Results Test) and GoogleOther, so a US-context
 * live test screenshotted the scroll-locked modal. */
const BOT_REGEX = /bot|crawl|spider|slurp|googlebot|inspectiontool|googleother/i;

class GeoPopup extends HTMLElement {
  connectedCallback() {
    onDocumentReady(() => this.#init(), { once: true });
  }

  #init() {
    if (BOT_REGEX.test(navigator.userAgent)) return;

    const country = window.Shopify?.country;
    if (!country) return;

    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    const storageKey = this.dataset.storageKey || 'sinter_geo_preference_v2';
    if (this.#readPreference(storageKey) !== null) return;

    const targets = (this.dataset.targetCountries || '')
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
    if (!targets.includes(country.toUpperCase())) return;

    const usUrl = this.dataset.usUrl || 'https://sinterusa.com';

    // The choice is forced — ESC must not dismiss the dialog. The UA's close watcher can
    // still force-close it (repeated ESC / Android back gesture: preventDefault on 'cancel'
    // is honored only once per user activation), so 'close' owns the scroll unlock — it
    // covers stay-global AND a force-close, which stores no preference, so the popup
    // legitimately re-shows on the next page view.
    dialog.addEventListener('cancel', (event) => event.preventDefault());
    dialog.addEventListener('close', () => unlockScroll(dialog));

    this.querySelector('[data-geo-action="continue-us"]')?.addEventListener('click', () => {
      this.#writePreference(storageKey, 'us');
      window.location.replace(usUrl + window.location.pathname + window.location.search);
    });

    this.querySelector('[data-geo-action="stay-global"]')?.addEventListener('click', () => {
      this.#writePreference(storageKey, 'global');
      dialog.close(); // the 'close' listener unlocks scroll
    });

    // Small delay so screen readers can process the landing page first (legacy timing).
    requestAnimationFrame(() => {
      setTimeout(() => {
        lockScroll(dialog);
        dialog.showModal();
      }, 300);
    });
  }

  /**
   * @param {string} storageKey
   * @returns {string | null}
   */
  #readPreference(storageKey) {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} storageKey
   * @param {'us' | 'global'} value
   */
  #writePreference(storageKey, value) {
    try {
      localStorage.setItem(storageKey, value);
    } catch {
      /* Safari private mode, sandboxed iframe — silently ignore */
    }
  }
}

if (!customElements.get('geo-popup')) {
  customElements.define('geo-popup', GeoPopup);
}
